// Product Card Quick Add - Drawer e Hover Variants

class VariantDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.modal-overlay');
    this.variantRadios = this.querySelectorAll('.variant-radio');

    // Store reference to original parent and variants JSON before any move
    this.originalParent = this.parentElement;
    const productItem = this.closest('.product-item');
    this.variantsJson = JSON.parse(productItem?.querySelector('.product-variants-json')?.textContent || '[]');

    this.init();
  }

  init() {
    // Close on overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('visible')) {
        this.close();
      }
    });

    this.variantRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.onVariantChange(e));
    });

    // Set initial availability state (skip for single size)
    const hasSingleSize = Array.from(this.variantRadios).some(r => r.dataset.singleSize === 'true');
    if (!hasSingleSize) {
      this.updateOptionAvailability();
    }
  }

  open() {
    // Move drawer to body to escape any stacking context issues
    document.body.appendChild(this);

    // Small delay to ensure DOM update before animation
    requestAnimationFrame(() => {
      this.classList.add('visible');
      this.classList.remove('invisible');
      this.classList.remove('pointer-none');
      document.body.style.overflow = 'hidden';
    });
  }

  close() {
    this.classList.remove('visible');
    this.classList.add('invisible');
    this.classList.add('pointer-none');
    document.body.style.overflow = '';

    // Move drawer back to original parent after animation
    setTimeout(() => {
      if (this.originalParent && this.parentElement === document.body) {
        this.originalParent.appendChild(this);
      }
    }, 300);
  }
  
  onVariantChange(event) {
    // Check if it's a single size product
    if (event.target.dataset.singleSize === 'true') {
      const variantId = event.target.dataset.variantId;
      if (variantId && event.target.dataset.autoAdd === 'true') {
        this.addToCart(variantId);
      }
      return;
    }
    
    const selectedOptions = {};
    
    this.variantRadios.forEach(radio => {
      if (radio.checked) {
        const position = radio.dataset.optionPosition;
        selectedOptions[`option${position}`] = radio.value;
      }
    });
    
    const matchedVariant = this.variantsJson.find(variant => {
      return Object.keys(selectedOptions).every(key => {
        return variant[key] === selectedOptions[key];
      });
    });
    
    if (matchedVariant) {
      // Auto-add if flag is set
      if (event.target.dataset.autoAdd === 'true' && matchedVariant.available) {
        this.addToCart(matchedVariant.id);
      }
      
      // Update availability states for other options
      this.updateOptionAvailability();
    }
  }
  
  addToCart(variantId) {
    // Get cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return;
    }
    
    // Close current drawer immediately
    this.close();
    
    // Get sections to render
    const sectionsToRender = cartDrawer.getSectionsToRender ? 
      cartDrawer.getSectionsToRender().map((section) => section.id) : 
      [cartDrawer.sectionId, 'cart-icon-bubble'];
    
    // Use Shopify's cart add API
    const cartAddUrl = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js';
    
    // Prepare request body
    const requestBody = {
      id: variantId,
      quantity: 1,
      sections: sectionsToRender,
      sections_url: window.location.pathname
    };
    
    // Add to cart using Shopify Cart API
    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(responseText => {
      const parsedState = JSON.parse(responseText);
      
      // Check for errors
      if (parsedState.status) {
        return;
      }
      
      // Render cart contents
      if (cartDrawer.renderContents) {
        cartDrawer.renderContents(parsedState);
      }

      // Check if mobile
      const isMobile = window.innerWidth <= 767 || window.matchMedia("(max-width: 767px)").matches;
      const mobilePopup = document.querySelector("mobile-cart-popup");

      if (isMobile && mobilePopup) {
        // Funzione per aprire il popup mobile
        const openMobilePopup = () => {
          fetch('/cart.js')
            .then(res => res.json())
            .then(cartData => {
              const addedItem = cartData.items.find(item => item.variant_id == variantId);
              if (addedItem) {
                mobilePopup.open({
                  variant: {
                    id: addedItem.variant_id,
                    title: addedItem.variant_title || "Default Title",
                    price: addedItem.price,
                    featured_image: addedItem.image ? { url: addedItem.image, alt: addedItem.title } : null,
                  },
                  product: {
                    title: addedItem.product_title,
                    url: addedItem.url,
                    handle: addedItem.handle,
                    featured_image: addedItem.image,
                  },
                  cartData: cartData,
                  quantity: addedItem.quantity,
                });
              }
            });
        };

        // Chiudi search se aperta, poi apri il popup
        const actionSearch = document.querySelector("action-search");
        const isSearchOpen = actionSearch && (
          actionSearch.classList.contains("active") ||
          !actionSearch.classList.contains("content-hidden")
        );

        if (isSearchOpen) {
          // Chiudi la search manualmente
          actionSearch.classList.remove("active");
          setTimeout(() => {
            actionSearch.classList.add("content-hidden");
            document.documentElement.classList.remove("open-modal");
            document.body.style.overflow = "";
            document.dispatchEvent(new CustomEvent("modal:closed"));
            // Apri il popup dopo che la search si è chiusa
            setTimeout(openMobilePopup, 50);
          }, 350);
        } else {
          openMobilePopup();
        }
      } else {
        // Su desktop: apri cart drawer
        setTimeout(() => {
          const cartIcon = document.querySelector('#cart-icon-bubble, #cart-icon-bubble-mobile');
          if (cartIcon) {
            cartIcon.click();
          } else {
            cartDrawer.classList.add('active');
            cartDrawer.classList.remove('invisible');
            cartDrawer.classList.remove('pointer-none');
            const overlay = cartDrawer.querySelector('modal-overlay');
            if (overlay) {
              overlay.classList.add('active');
            }
            const modalInner = cartDrawer.querySelector('.modal-inner');
            if (modalInner) {
              modalInner.classList.add('active');
            }
            document.body.style.overflow = 'hidden';
          }
        }, 200);
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
    })
    .catch(error => {
      // Silent fail
    });
  }
  
  updateOptionAvailability() {
    // Check availability for each option independently
    this.variantRadios.forEach(radio => {
      const position = radio.dataset.optionPosition;
      const value = radio.value;
      
      // Find if there's any available variant with this option value
      const hasAvailableVariant = this.variantsJson.some(variant => {
        return variant[`option${position}`] === value && variant.available;
      });
      
      radio.disabled = !hasAvailableVariant;
      
      // Update label class
      const label = this.querySelector(`label[for="${radio.id}"]`);
      if (label) {
        if (hasAvailableVariant) {
          label.classList.remove('disabled');
        } else {
          label.classList.add('disabled');
        }
      }
    });
  }
}

customElements.define('variant-drawer', VariantDrawer);

// Product Form with Quick Add functionality  
class QuickAddHover extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.variantRadios = this.querySelectorAll('.variant-radio-hover');
    this.variantInput = this.querySelector('.variant-id-input-hover');
    
    const productItem = this.closest('.product-item');
    this.variantsJson = JSON.parse(productItem?.querySelector('.product-variants-json-hover')?.textContent || '[]');
    
    this.variantRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.onVariantChange(e));
      
      // Add click listener to label for single size products
      if (radio.dataset.singleSize === 'true') {
        const label = this.querySelector(`label[for="${radio.id}"]`);
        if (label) {
          label.addEventListener('click', (e) => {
            e.preventDefault();
            const variantId = radio.dataset.variantId;
            if (variantId) {
              this.addToCart(variantId);
            }
          });
        }
      }
    });
    
    // Set initial availability state (skip for single size)
    const hasSingleSize = Array.from(this.variantRadios).some(r => r.dataset.singleSize === 'true');
    if (!hasSingleSize) {
      this.updateOptionAvailability();
    }
  }
  
  onVariantChange(event) {
    // Check if it's a single size product
    if (event.target.dataset.singleSize === 'true') {
      const variantId = event.target.dataset.variantId;
      if (variantId && event.target.dataset.autoAdd === 'true') {
        this.addToCart(variantId);
      }
      return;
    }
    
    const selectedOptions = {};
    
    this.variantRadios.forEach(radio => {
      if (radio.checked) {
        const position = radio.dataset.optionPosition;
        selectedOptions[`option${position}`] = radio.value;
      }
    });
    
    const matchedVariant = this.variantsJson.find(variant => {
      return Object.keys(selectedOptions).every(key => {
        return variant[key] === selectedOptions[key];
      });
    });
    
    if (matchedVariant) {
      // Auto-add if flag is set
      if (event.target.dataset.autoAdd === 'true' && matchedVariant.available) {
        this.addToCart(matchedVariant.id);
      }
      
      // Update availability states
      this.updateOptionAvailability();
    }
  }
  
  addToCart(variantId) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      return;
    }
    
    // Get sections to render
    const sectionsToRender = cartDrawer.getSectionsToRender ? 
      cartDrawer.getSectionsToRender().map((section) => section.id) : 
      [cartDrawer.sectionId, 'cart-icon-bubble'];
    
    // Use Shopify's cart add API
    const cartAddUrl = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js';
    
    // Prepare request body
    const requestBody = {
      id: variantId,
      quantity: 1,
      sections: sectionsToRender,
      sections_url: window.location.pathname
    };
    
    // Add to cart using Shopify Cart API
    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.text();
    })
    .then(responseText => {
      const parsedState = JSON.parse(responseText);
      
      // Check for errors
      if (parsedState.status) {
        return;
      }
      
      // Render cart contents
      if (cartDrawer.renderContents) {
        cartDrawer.renderContents(parsedState);
      }

      // Check if mobile
      const isMobile = window.innerWidth <= 767 || window.matchMedia("(max-width: 767px)").matches;
      const mobilePopup = document.querySelector("mobile-cart-popup");

      if (isMobile && mobilePopup) {
        // Funzione per aprire il popup mobile
        const openMobilePopup = () => {
          fetch('/cart.js')
            .then(res => res.json())
            .then(cartData => {
              const addedItem = cartData.items.find(item => item.variant_id == variantId);
              if (addedItem) {
                mobilePopup.open({
                  variant: {
                    id: addedItem.variant_id,
                    title: addedItem.variant_title || "Default Title",
                    price: addedItem.price,
                    featured_image: addedItem.image ? { url: addedItem.image, alt: addedItem.title } : null,
                  },
                  product: {
                    title: addedItem.product_title,
                    url: addedItem.url,
                    handle: addedItem.handle,
                    featured_image: addedItem.image,
                  },
                  cartData: cartData,
                  quantity: addedItem.quantity,
                });
              }
            });
        };

        // Chiudi search se aperta, poi apri il popup
        const actionSearch = document.querySelector("action-search");
        const isSearchOpen = actionSearch && (
          actionSearch.classList.contains("active") ||
          !actionSearch.classList.contains("content-hidden")
        );

        if (isSearchOpen) {
          // Chiudi la search manualmente
          actionSearch.classList.remove("active");
          setTimeout(() => {
            actionSearch.classList.add("content-hidden");
            document.documentElement.classList.remove("open-modal");
            document.body.style.overflow = "";
            document.dispatchEvent(new CustomEvent("modal:closed"));
            // Apri il popup dopo che la search si è chiusa
            setTimeout(openMobilePopup, 50);
          }, 350);
        } else {
          openMobilePopup();
        }
      } else {
        // Su desktop: apri cart drawer
        setTimeout(() => {
          const cartIcon = document.querySelector('#cart-icon-bubble, #cart-icon-bubble-mobile');
          if (cartIcon) {
            cartIcon.click();
          } else {
            cartDrawer.classList.add('active');
            cartDrawer.classList.remove('invisible');
            cartDrawer.classList.remove('pointer-none');
            const overlay = cartDrawer.querySelector('modal-overlay');
            if (overlay) {
              overlay.classList.add('active');
            }
            const modalInner = cartDrawer.querySelector('.modal-inner');
            if (modalInner) {
              modalInner.classList.add('active');
            }
            document.body.style.overflow = 'hidden';
          }
        }, 200);
      }

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
    })
    .catch(error => {
      // Silent fail
    });
  }
  
  updateOptionAvailability() {
    // Check availability for each option independently
    this.variantRadios.forEach(radio => {
      const position = radio.dataset.optionPosition;
      const value = radio.value;
      
      // Find if there's any available variant with this option value
      const hasAvailableVariant = this.variantsJson.some(variant => {
        return variant[`option${position}`] === value && variant.available;
      });
      
      radio.disabled = !hasAvailableVariant;
      
      // Update label class
      const label = this.querySelector(`label[for="${radio.id}"]`);
      if (label) {
        if (hasAvailableVariant) {
          label.classList.remove('disabled');
        } else {
          label.classList.add('disabled');
        }
      }
    });
  }
}

customElements.define('quick-add-hover', QuickAddHover);

// Quick Add Drawer - Placeholder (non fa nulla, solo container)
class QuickAddDrawer extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('quick-add-drawer', QuickAddDrawer);

// Handle click on + button to open drawer
document.addEventListener('click', (e) => {
  const addBtn = e.target.closest('.product-card-add-btn');
  if (!addBtn) return;
  
  const productItem = addBtn.closest('.product-item');
  if (!productItem) return;
  
  // Always open drawer (works for both multi-variant and single-size)
  const drawer = productItem.querySelector('variant-drawer');
  if (drawer) {
    e.preventDefault();
    drawer.open();
  }
});
